import type { Identifier } from "ra-core";
import type { ChargeOnSendResult } from "@/modules/billing/chargeOnSendTypes";
import type { TicketMessage } from "@/modules/types";
import type { TicketMergeFieldOverrides } from "@/modules/tickets/ticketMergeCompare";
import type { TicketReplyAttachment } from "@/modules/tickets/uploadTicketAttachment";
import {
  invokeEdgeFunction,
  readEdgeFunctionErrorMessage,
} from "../invokeEdgeFunction";

export const ticketsProvider = {
  async replyTicket({
    ticketId,
    body,
    htmlBody,
    isInternalNote = false,
    attachments = [],
    toEmails,
    ccEmails,
    nextStatus,
  }: {
    ticketId: Identifier;
    body: string;
    htmlBody?: string;
    isInternalNote?: boolean;
    attachments?: TicketReplyAttachment[];
    toEmails?: string[];
    ccEmails?: string[];
    nextStatus?: string;
  }) {
    const { data, error } = await invokeEdgeFunction<{
      message: TicketMessage;
      email_sent?: boolean;
      email_skipped?: boolean;
      is_internal_note?: boolean;
    }>("reply_ticket", {
      method: "POST",
      body: {
        ticket_id: Number(ticketId),
        body,
        html_body: htmlBody,
        is_internal_note: isInternalNote,
        attachments,
        to_emails: toEmails,
        ...(ccEmails?.length ? { cc_emails: ccEmails } : {}),
        ...(nextStatus?.trim() ? { next_status: nextStatus.trim() } : {}),
      },
    });

    if (error || !data?.message) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(error, "Failed to send reply")
          : "Failed to send reply",
      );
    }

    return data;
  },

  async mergeTickets({
    primaryTicketId,
    mergeTicketIds,
    fieldOverrides,
  }: {
    primaryTicketId: Identifier;
    mergeTicketIds: Identifier[];
    fieldOverrides?: TicketMergeFieldOverrides;
  }) {
    const { data, error } = await invokeEdgeFunction<{
      primary_ticket_id: number;
      merged_ticket_ids: number[];
    }>("merge_tickets", {
      method: "POST",
      body: {
        primary_ticket_id: Number(primaryTicketId),
        merge_ticket_ids: mergeTicketIds.map((id) => Number(id)),
        ...(fieldOverrides ? { field_overrides: fieldOverrides } : {}),
      },
    });

    if (error || !data?.primary_ticket_id) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(error, "Failed to merge tickets")
          : "Failed to merge tickets",
      );
    }

    return data;
  },

  /** Undo a merge: the source ticket gets its messages back and reopens. */
  async unmergeTicket({
    primaryTicketId,
    sourceTicketId,
    messageIds,
  }: {
    primaryTicketId: Identifier;
    sourceTicketId: Identifier;
    messageIds: Identifier[];
  }) {
    const { data, error } = await invokeEdgeFunction<{
      primary_ticket_id: number;
      source_ticket_id: number;
      moved_message_ids: number[];
    }>("unmerge_ticket", {
      method: "POST",
      body: {
        primary_ticket_id: Number(primaryTicketId),
        source_ticket_id: Number(sourceTicketId),
        message_ids: messageIds.map((id) => Number(id)),
      },
    });
    if (error || !data?.source_ticket_id) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Failed to separate ticket",
            )
          : "Failed to separate ticket",
      );
    }
    return data;
  },

  async moveTicketMessages({
    sourceTicketId,
    messageIds,
    targetTicketId,
    createNew,
    subject,
    status,
  }: {
    sourceTicketId: Identifier;
    messageIds: Identifier[];
    targetTicketId?: Identifier;
    createNew?: boolean;
    subject?: string;
    status?: string;
  }) {
    const { data, error } = await invokeEdgeFunction<{
      source_ticket_id: number;
      target_ticket_id: number;
      moved_message_ids: number[];
      created_new: boolean;
    }>("move_ticket_messages", {
      method: "POST",
      body: {
        source_ticket_id: Number(sourceTicketId),
        message_ids: messageIds.map((id) => Number(id)),
        ...(createNew
          ? {
              create_new: true,
              ...(subject?.trim() ? { subject: subject.trim() } : {}),
              ...(status?.trim() ? { status: status.trim() } : {}),
            }
          : { target_ticket_id: Number(targetTicketId) }),
      },
    });

    if (error || !data?.target_ticket_id) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Failed to move ticket messages",
            )
          : "Failed to move ticket messages",
      );
    }

    return data;
  },

  async createTicketInvoice({
    ticketId,
    baseUrl,
    message,
  }: {
    ticketId: Identifier;
    baseUrl?: string;
    message?: string;
  }) {
    const { data, error } = await invokeEdgeFunction<{
      invoice: Record<string, unknown>;
      payment_url: string;
      to: string;
    }>("create_ticket_invoice", {
      method: "POST",
      body: {
        ticket_id: Number(ticketId),
        ...(baseUrl ? { base_url: baseUrl } : {}),
        ...(message ? { message } : {}),
      },
    });

    if (error || !data?.invoice) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Failed to create ticket invoice",
            )
          : "Failed to create ticket invoice",
      );
    }

    return data;
  },

  async prepareTicketInvoice({
    ticketId,
    baseUrl,
  }: {
    ticketId: Identifier;
    baseUrl?: string;
  }) {
    const { data, error } = await invokeEdgeFunction<{
      invoice: Record<string, unknown>;
      line_items: Record<string, unknown>[];
      payment_url: string;
      to: string;
    }>("prepare_ticket_invoice", {
      method: "POST",
      body: {
        ticket_id: Number(ticketId),
        ...(baseUrl ? { base_url: baseUrl } : {}),
      },
    });

    if (error || !data?.invoice) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Failed to prepare invoice",
            )
          : "Failed to prepare invoice",
      );
    }

    return data;
  },

  async sendTicketInvoice({
    ticketId,
    baseUrl,
    message,
    subject,
    recipientEmail,
    smsTo,
    sendSms,
    chargePaymentMethodId,
  }: {
    ticketId: Identifier;
    baseUrl?: string;
    message?: string;
    subject?: string;
    recipientEmail?: string;
    smsTo?: string;
    sendSms?: boolean;
    /** Charge this saved method instead of emailing a pay link. */
    chargePaymentMethodId?: string | null;
  }) {
    const { data, error } = await invokeEdgeFunction<{
      invoice: Record<string, unknown>;
      payment_url: string;
      to: string;
      sms_sent?: boolean;
      sms_skipped?: boolean;
      charge?: ChargeOnSendResult | null;
    }>("send_ticket_invoice", {
      method: "POST",
      body: {
        ticket_id: Number(ticketId),
        ...(chargePaymentMethodId
          ? { charge_payment_method_id: chargePaymentMethodId }
          : {}),
        ...(baseUrl ? { base_url: baseUrl } : {}),
        ...(message ? { message } : {}),
        ...(subject ? { subject } : {}),
        ...(recipientEmail?.trim()
          ? { recipient_email: recipientEmail.trim() }
          : {}),
        ...(smsTo ? { sms_to: smsTo } : {}),
        ...(sendSms ? { send_sms: true } : {}),
      },
    });

    if (error || !data?.invoice) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(error, "Failed to send invoice")
          : "Failed to send invoice",
      );
    }

    return data;
  },

  async cancelTicketInvoiceDraft({ ticketId }: { ticketId: Identifier }) {
    const { data, error } = await invokeEdgeFunction<{
      cancelled?: boolean;
      skipped?: boolean;
    }>("cancel_ticket_invoice", {
      method: "POST",
      body: { ticket_id: Number(ticketId) },
    });

    if (error) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Could not cancel invoice draft",
            )
          : "Could not cancel invoice draft",
      );
    }

    return data;
  },

  async startNewTicketInvoice({ ticketId }: { ticketId: Identifier }) {
    const { data, error } = await invokeEdgeFunction<{
      previous_invoice_number: string | null;
      unbilled_deliverable_count: number;
      already_in_new_cycle?: boolean;
    }>("start_new_ticket_invoice", {
      method: "POST",
      body: { ticket_id: Number(ticketId) },
    });

    if (error || !data) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Could not start a new invoice",
            )
          : "Could not start a new invoice",
      );
    }

    return data;
  },

  async deliverTicket({ ticketId }: { ticketId: Identifier }) {
    const { data, error } = await invokeEdgeFunction<{
      delivered: boolean;
      duplicate?: boolean;
      ticket_id?: number;
      ticket_status?: string;
      results?: Array<{
        ticketId: number;
        delivered: boolean;
        ticket_status?: string;
      }>;
    }>("deliver_ticket", {
      method: "POST",
      body: { ticket_id: Number(ticketId) },
    });

    if (error || !(data?.delivered || data?.duplicate)) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(error, "Could not deliver files")
          : "Could not deliver files",
      );
    }

    const fromResults = data.results?.find(
      (row) => Number(row.ticketId) === Number(ticketId),
    )?.ticket_status;

    return {
      ...data,
      ticket_status: data.ticket_status ?? fromResults,
    };
  },

  async prepareCombinedTicketInvoice({
    ticketIds,
    baseUrl,
    recipientEmail,
  }: {
    ticketIds: Identifier[];
    baseUrl?: string;
    recipientEmail?: string;
  }) {
    const { data, error } = await invokeEdgeFunction<{
      invoice: Record<string, unknown>;
      line_items: Record<string, unknown>[];
      payment_url: string;
      to: string;
      ticket_ids: number[];
    }>("prepare_combined_ticket_invoice", {
      method: "POST",
      body: {
        ticket_ids: ticketIds.map((id) => Number(id)),
        ...(baseUrl ? { base_url: baseUrl } : {}),
        ...(recipientEmail?.trim()
          ? { recipient_email: recipientEmail.trim() }
          : {}),
      },
    });

    if (error || !data?.invoice) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Failed to prepare combined invoice",
            )
          : "Failed to prepare combined invoice",
      );
    }

    return data;
  },

  async sendCombinedTicketInvoice({
    ticketIds,
    baseUrl,
    message,
    subject,
    smsTo,
    sendSms,
    recipientEmail,
    chargePaymentMethodId,
  }: {
    ticketIds: Identifier[];
    baseUrl?: string;
    message?: string;
    subject?: string;
    smsTo?: string;
    sendSms?: boolean;
    recipientEmail?: string;
    /** Charge this saved method instead of emailing a pay link. */
    chargePaymentMethodId?: string | null;
  }) {
    const { data, error } = await invokeEdgeFunction<{
      invoice: Record<string, unknown>;
      payment_url: string;
      to: string;
      ticket_ids: number[];
      charge?: ChargeOnSendResult | null;
    }>("send_combined_ticket_invoice", {
      method: "POST",
      body: {
        ticket_ids: ticketIds.map((id) => Number(id)),
        ...(chargePaymentMethodId
          ? { charge_payment_method_id: chargePaymentMethodId }
          : {}),
        ...(baseUrl ? { base_url: baseUrl } : {}),
        ...(message ? { message } : {}),
        ...(subject ? { subject } : {}),
        ...(smsTo ? { sms_to: smsTo } : {}),
        ...(sendSms ? { send_sms: true } : {}),
        ...(recipientEmail?.trim()
          ? { recipient_email: recipientEmail.trim() }
          : {}),
      },
    });

    if (error || !data?.invoice) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Failed to send combined invoice",
            )
          : "Failed to send combined invoice",
      );
    }

    return data;
  },

  async cancelCombinedTicketInvoiceDraft({
    ticketIds,
  }: {
    ticketIds: Identifier[];
  }) {
    const { data, error } = await invokeEdgeFunction<{
      cancelled?: boolean;
      skipped?: boolean;
    }>("cancel_combined_ticket_invoice", {
      method: "POST",
      body: { ticket_ids: ticketIds.map((id) => Number(id)) },
    });

    if (error) {
      throw new Error(
        error
          ? await readEdgeFunctionErrorMessage(
              error,
              "Could not cancel combined invoice draft",
            )
          : "Could not cancel combined invoice draft",
      );
    }

    return data;
  },
};
